import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, MapPin, Building2, Gavel, User, Bike, Package, ArrowRight, Phone } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BRAND } from "../App";

const services = [
  { icon: MapPin, title: "Local Vehicle Towing", desc: "Fast and reliable local vehicle towing and transportation services for your immediate needs within our service area." },
  { icon: Truck, title: "Long-Distance Transport", desc: "Safe and dependable long-distance vehicle transportation across state lines. We handle your vehicle like it's our own." },
  { icon: Building2, title: "Dealership Transport", desc: "Professional vehicle transportation solutions for dealerships. Fast turnaround and competitive rates for dealer fleets." },
  { icon: Gavel, title: "Auction Pickup & Delivery", desc: "Pick-up and delivery service from auctions. Fast scheduling to get your newly purchased vehicle delivered." },
  { icon: User, title: "Private Vehicle Transport", desc: "Personal vehicle transportation with care and attention. From family moves to one-time transport needs." },
  { icon: Bike, title: "Motorcycle Transport", desc: "Specialized motorcycle transportation with proper tie-downs and handling to protect your bike." },
];

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Banner */}
      <section className="bg-black text-white pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black to-black/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-4">Our Services</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-4">
              Transportation Services<br />
              <span className="text-[#E31E24]">Done Right</span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl">
              Professional vehicle transportation and towing services for every need. Reliable, safe, and competitively priced.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-gray-50 p-8 border-l-4 border-[#E31E24] hover:bg-black hover:text-white transition-all"
              >
                <div className="w-14 h-14 bg-[#E31E24] flex items-center justify-center mb-6">
                  <service.icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-black uppercase mb-3">{service.title}</h3>
                <p className="text-sm leading-relaxed opacity-80">{service.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Additional Services Note */}
          <div className="mt-12 bg-black text-white p-8 flex flex-col sm:flex-row items-start gap-6">
            <Package size={48} className="text-[#E31E24] flex-shrink-0" />
            <div>
              <h3 className="text-2xl font-black uppercase mb-3">Specialty Transport Requests</h3>
              <p className="text-white/80 mb-4">
                Have a unique transport need? We handle additional specialty transport requests subject to equipment capacity and approval. Contact Shabi directly to discuss.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={BRAND.phoneHref} className="bg-[#E31E24] text-white font-bold uppercase px-6 py-3 hover:bg-white hover:text-black transition-all flex items-center gap-2">
                  <Phone size={18} />
                  Call {BRAND.phone}
                </a>
                <Link to="/get-quote" className="border border-white text-white font-bold uppercase px-6 py-3 hover:bg-white hover:text-black transition-all">
                  Request a Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#E31E24] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black uppercase mb-4">Ready to Get Started?</h2>
          <p className="text-white/90 text-lg mb-8">Submit your transport request and we'll handle the rest.</p>
          <Link to="/get-quote" className="inline-flex items-center gap-2 bg-black text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-black transition-all">
            Request Transport Now
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
